import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import { useEffectiveUid, useIsAdminMode } from '../auth/TargetUidContext';

export interface GoogleAuthStatus {
  connected: boolean;
  calendarId?: string;
  scopes?: string[];
  hasRefreshToken?: boolean;
  watchExpiresAt?: number | null;
}

export function useGoogleAuthStatus() {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useQuery<GoogleAuthStatus>({
    queryKey: ['google-auth-status', targetUid] as const,
    enabled: !!user && !!targetUid,
    staleTime: 30_000,
    queryFn: async () => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<{ uid: string }, GoogleAuthStatus>(
          functions,
          'adminGetGoogleAuthStatus',
        );
        const r = await fn({ uid: targetUid });
        return r.data;
      }
      const fn = httpsCallable<unknown, GoogleAuthStatus>(functions, 'getGoogleAuthStatus');
      const r = await fn({});
      return r.data;
    },
  });
}

export function useGoogleCalendars() {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  const status = useGoogleAuthStatus();
  return useQuery({
    queryKey: ['google-calendars', targetUid] as const,
    enabled: !!user && !!status.data?.connected && !!targetUid,
    staleTime: 60_000,
    queryFn: async () => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<
          { uid: string },
          { items: Array<{ id: string; summary: string; primary: boolean; accessRole: string }> }
        >(functions, 'adminListGoogleCalendars');
        const r = await fn({ uid: targetUid });
        return r.data.items;
      }
      const fn = httpsCallable<
        unknown,
        { items: Array<{ id: string; summary: string; primary: boolean; accessRole: string }> }
      >(functions, 'listGoogleCalendars');
      const r = await fn({});
      return r.data.items;
    },
  });
}

export function useDisconnectGoogle() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useMutation({
    mutationFn: async () => {
      if (adminMode) {
        // Disconnect on behalf of another user is intentionally not
        // supported — the user must revoke access themselves.
        throw new Error(
          'Im Admin-Modus kann der Google-Kalender nicht im Namen des Benutzers getrennt werden.',
        );
      }
      const fn = httpsCallable<unknown, { ok: true }>(functions, 'googleOauthDisconnect');
      await fn({});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['google-auth-status', targetUid] });
      qc.invalidateQueries({ queryKey: ['google-calendars', targetUid] });
    },
  });
}

export function useSetActiveCalendar() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useMutation({
    mutationFn: async (calendarId: string) => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<{ uid: string; calendarId: string }, { ok: true }>(
          functions,
          'adminSetActiveCalendar',
        );
        await fn({ uid: targetUid, calendarId });
        return;
      }
      const fn = httpsCallable<{ calendarId: string }, { ok: true }>(functions, 'setActiveCalendar');
      await fn({ calendarId });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['google-auth-status', targetUid] }),
  });
}

export function useSyncCalendarNow() {
  const adminMode = useIsAdminMode();
  return useMutation({
    mutationFn: async () => {
      if (adminMode) {
        throw new Error(
          'Im Admin-Modus kann der Kalender nicht im Namen des Benutzers manuell synchronisiert werden.',
        );
      }
      const fn = httpsCallable<unknown, { ok: true; updated: number; deleted: number }>(
        functions,
        'syncCalendarNow',
      );
      const r = await fn({});
      return r.data;
    },
  });
}

export function useRegisterCalendarWatch() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useMutation({
    mutationFn: async () => {
      if (adminMode) {
        throw new Error(
          'Im Admin-Modus kann das Kalender-Watch nicht im Namen des Benutzers registriert werden.',
        );
      }
      const fn = httpsCallable<unknown, { ok: true; expiresAt: number }>(
        functions,
        'registerCalendarWatch',
      );
      const r = await fn({});
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['google-auth-status', targetUid] }),
  });
}

/**
 * Opens the Google OAuth consent popup. Two-step:
 *   1. Call `googleOauthStart` (callable) — server mints a single-use
 *      state nonce and returns the consent URL. The Firebase ID token
 *      stays in the Authorization header of the callable, never in a
 *      query string.
 *   2. Open the URL in a popup. The popup eventually `postMessage()`s
 *      back from the same app origin with `{source, ok}`.
 *
 * The returned promise resolves on either popup-close OR a matching
 * message. The window-message listener and the popup-close-poll interval
 * are both cleaned up unconditionally on settle, even when the user
 * navigates away mid-flow.
 */
export async function startGoogleOauthPopup(): Promise<{ ok: boolean }> {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign-in required.');
  const start = httpsCallable<unknown, { url: string; state: string }>(
    functions,
    'googleOauthStart',
  );
  const startResp = await start({});
  const popup = window.open(startResp.data.url, 'cp3-google-oauth', 'width=520,height=640,popup=yes');
  if (!popup) throw new Error('Popup blockiert. Bitte Pop-Ups erlauben.');

  return new Promise((resolve) => {
    let interval: number | null = null;
    function cleanup() {
      window.removeEventListener('message', onMessage);
      if (interval !== null) {
        window.clearInterval(interval);
        interval = null;
      }
    }
    function onMessage(ev: MessageEvent<unknown>) {
      // Pin the listener to our app origin — the server's result page
      // always posts to https://cp-3.web.app, so cross-origin messages
      // from rogue popups can't satisfy this.
      if (ev.origin !== window.location.origin && ev.origin !== 'https://cp-3.web.app') {
        return;
      }
      const data = ev.data as { source?: string; ok?: boolean };
      if (data?.source === 'cp3-google-oauth') {
        cleanup();
        resolve({ ok: !!data.ok });
      }
    }
    window.addEventListener('message', onMessage);
    interval = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
        resolve({ ok: false });
      }
    }, 800);
  });
}
